import { createFileRoute } from "@tanstack/react-router";
import { routeMeta, leafCanonicalLink } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { CollectionFlagship, type FlagshipContent } from "@/components/CollectionFlagship";
import { getCollection } from "@/data/collections";

const collection = getCollection("ranger-series")!;

const content: FlagshipContent = {
  eyebrow: "Signature Collection · Care · Maintenance · Protection",
  heroKicker:
    "Care & Maintenance Plans · San Antonio · Austin · New Braunfels · Seguin · Statewide Texas",
  badgeSubline:
    "Annual Cleaning • Fabric Protectant • Motor & Hardware Inspection • Storm Prep • Priority Scheduling",
  lifestyleIntro:
    "Shade doesn't fail overnight — it fails from neglect. Ranger Series™ keeps your fabric clean, your motors tested and your frame tight so you never end up needing a rescue.",
  lifestyleBody:
    "The Ranger Series is Texas Made Shade's care and maintenance division. A Ranger Care™ membership puts your awning, retractable system, drop curtain or cover on a yearly schedule: we clean and re-protect the fabric, inspect hardware, arms, motors and sensors, tighten and touch up the frame, prep the system before storm season and put you at the front of the service line when you do need us. Already torn, storm-damaged or motor-dead? That's repair work — the Revival Series™ handles recovers, refabrics, rewelds and motor replacement.",
  solutionsHeading: "What's under the Ranger Series™ badge",
  solutions: [
    { title: "Ranger Care™ Membership", desc: "Annual maintenance plan for awnings, retractables, curtains and covers.", to: "/ranger-care" },
    { title: "Fabric Cleaning & Protectant", desc: "Proper cleaning and re-treatment that keeps Sunbrella® fabric shedding water and resisting mildew.", to: "/ranger-care" },
    { title: "Hardware & Frame Inspection", desc: "Fasteners, brackets, arms and welds checked and tightened before they become failures.", to: "/ranger-care" },
    { title: "Motor & Sensor Testing", desc: "Motors, remotes, wind and sun sensors tested and re-calibrated on motorized systems.", to: "/lone-star-elite" },
    { title: "Storm Prep & Seasonal Retraction", desc: "Pre-season checks and guidance so wind events don't turn into insurance claims.", to: "/ranger-care" },
    { title: "Priority Service Scheduling", desc: "Members go to the front of the line when Texas weather fills the schedule.", to: "/ranger-care" },
    { title: "Cleaning Supplies & Care Kits", desc: "Official Sunbrella® cleaners and protectants for between-visit upkeep.", to: "/shop" },
    { title: "Need a repair instead?", desc: "Torn fabric, dead motors, bent frames and storm damage are Revival Series™ work.", to: "/revival-series" },
    { title: "Awning & Canopy Recover", desc: "Frame is sound but the canvas is spent — that's a Revival Series™ recover.", to: "/recover-awnings" },
    { title: "Fabrics & Colors", desc: "See the fabrics we clean, protect and eventually recover with.", to: "/fabrics" },
  ],
  trustBadges: [
    "Maintenance, not just repair",
    "Members get priority scheduling",
    "Sunbrella® approved care products",
    "Almost 30 yrs experience",
  ],
  faqs: [
    {
      q: "What is the difference between Ranger Series™ and Revival Series™?",
      a: "Ranger Series is preventive care — scheduled cleaning, protectant, inspection and storm prep through a Ranger Care™ membership. Revival Series is corrective work — recovers, refabrics, motor replacement, welding and storm-damage restoration. Ranger keeps it healthy; Revival brings it back.",
    },
    {
      q: "What does a Ranger Care™ membership include?",
      a: "An annual visit with fabric cleaning and protectant re-treatment, hardware and frame inspection, motor and sensor testing on motorized systems, storm-prep guidance, priority scheduling and member pricing on recover and repair work.",
    },
    {
      q: "Do you maintain shade you didn't build?",
      a: "Yes. If the system is structurally sound and parts are available, we'll put it on a care plan regardless of who built or installed it.",
    },
    {
      q: "How often should outdoor fabric be cleaned and re-protected?",
      a: "In Texas sun and pollen, once a year is the practical baseline for cleaning and protectant, with a rinse between visits. Heavy tree cover, coastal salt air or restaurant grease exposure can call for more.",
    },
    {
      q: "Does maintenance actually extend the life of an awning?",
      a: "It does. Most premature failures we see are mildew set into dirty fabric, corrosion at loose hardware and motors run with worn arms — all things a yearly inspection catches while they are still cheap to fix.",
    },
    {
      q: "How do I start a plan?",
      a: "Send photos of the system and rough measurements through the form below, or call (830) 390-9588. We'll confirm what it covers and quote the annual plan.",
    },
  ],
};

export const Route = createFileRoute("/ranger-series")({
  head: (ctx) => ({
    meta: routeMeta(ctx, {
      title: "Ranger Series™ — Awning Care, Cleaning & Maintenance Plans | Texas Made Shade",
      description:
        "Ranger Series™ — Ranger Care™ maintenance plans for awnings, retractables and covers: annual fabric cleaning and protectant, hardware and motor inspection, storm prep, priority service. San Antonio, Austin, New Braunfels, Seguin.",
      keywords:
        "awning maintenance plan texas, awning cleaning san antonio, sunbrella fabric cleaning austin, retractable awning maintenance new braunfels, awning inspection seguin, awning fabric protectant texas, shade maintenance membership, storm prep awning texas, awning care plan",
    }),
    links: leafCanonicalLink(ctx, collection.path),
    scripts: serviceHeadScripts({
      path: collection.path,
      serviceName: "Ranger Series Awning Care & Maintenance Plans",
      serviceType: "Awning cleaning, fabric protectant, inspection & maintenance memberships",
      description: collection.description,
      faqs: content.faqs,
      breadcrumbName: "Ranger Series — Care, Maintenance & Protection",
    }),
  }),
  component: () => <CollectionFlagship collection={collection} content={content} />,
});
